import { observer } from 'mobx-react';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { useClickTracker } from '@shared-ui/clickstream-analytics-context';

import { SearchCriteriaModel } from 'stores/models';

import { CollapseHeight } from 'constants/collapse-height';
import { FilterModel, FilterOptionModel, SortAndFilterModel } from 'components/common/search-form/fields/filter-types';
import { UitkHeadingSize } from 'uitk-react-text/dist/types';

import { UitkExpandoPeek } from 'uitk-react-expando';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkSpacing } from 'uitk-react-spacing';

import { getCollapseHeight } from 'utils/get-collapse-height';

import { ActionDetailsType } from '__generated__/api';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { RadioGroupExpandoList } from './radio-group-expando-list';

const RadioButtonTracked = ClickTracker(UitkRadio);

const RadioGroupComponent = ({
  headerSize,
  rfrrSuffix,
  model,
  modelKey,
  sortAndFilterOptions,
  updateModel,
  handleChange,
}: {
  headerSize: UitkHeadingSize;
  rfrrSuffix?: () => void;
  model: SearchCriteriaModel;
  modelKey: string;
  sortAndFilterOptions: SortAndFilterModel;
  updateModel: any;
  handleChange?: (evt) => void;
}) => {
  const [viewExpanded, setViewExpanded] = React.useState(false);
  const track = useClickTracker();

  const radioChange = evt => {
    const target = evt.target;
    const { value } = target;

    const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const radioGroupName: string = radioGroup && radioGroup.title;
    const modelValue = model && model[radioGroupName];

    if (!(typeof updateModel === 'function') || typeof modelValue === 'undefined') {
      return;
    }

    updateModel({
      target: {
        name: radioGroupName,
        value,
      },
    });
  };

  const radioGroup = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const radioGroupOptions = radioGroup && radioGroup.options;
  const radioGroupName: string = radioGroup && radioGroup.title;
  const showCollapseState = radioGroup?.dropdownOptions?.isDropDownVisible;
  const showListState = radioGroup?.showListOptions;
  const collapseHeight =
    showListState && showListState.hiddenThreshold ? getCollapseHeight(showListState.hiddenThreshold) : CollapseHeight.TEN_HEIGHT;

  if (!radioGroupOptions || radioGroupOptions.length === 0) {
    return null;
  }

  return (
    <>
      {showCollapseState ? (
        <RadioGroupExpandoList
          radioGroup={radioGroup}
          handleChange={handleChange || radioChange}
          model={model}
          modelKey={modelKey}
          sortAndFilterOptions={sortAndFilterOptions}
          rfrrSuffix={rfrrSuffix}
          isVisible={showCollapseState === ActionDetailsType.OPEN}
        />
      ) : (
        <UitkSpacing margin={{ blockstart: 'six' }}>
          <fieldset>
            <UitkSpacing margin={{ blockend: 'two' }}>
              <legend id={`${radioGroupName}-legend`} className="filter-title">
                <UitkHeading size={headerSize} tag="h4">
                  {radioGroup.label}
                </UitkHeading>
              </legend>
            </UitkSpacing>
            {showListState.hiddenThreshold && radioGroupOptions.length > showListState.hiddenThreshold ? (
              <div data-stid="radio-group-expando-peek">
                <UitkExpandoPeek
                  collapseHeight={collapseHeight}
                  collapseLabel={showListState.seeLessLabel}
                  expandLabel={showListState.seeMoreLabel}
                  handleClick={() => setViewExpanded(!viewExpanded)}
                  analytics={{
                    id: radioGroup.title,
                    callback: id => {
                      const idArr = id.split('.');
                      track(`${idArr[0]}.ViewAllFilters.${idArr[1]}`, `${radioGroup.title}.ViewAllFilters`);
                    },
                  }}
                  isVisible={viewExpanded}
                  hideScrim
                >
                  <Radios
                    handleChange={handleChange || radioChange}
                    model={model}
                    modelKey={modelKey}
                    sortAndFilterOptions={sortAndFilterOptions}
                    rfrrSuffix={rfrrSuffix}
                  />
                </UitkExpandoPeek>
              </div>
            ) : (
              <Radios
                handleChange={handleChange || radioChange}
                model={model}
                modelKey={modelKey}
                sortAndFilterOptions={sortAndFilterOptions}
                rfrrSuffix={rfrrSuffix}
              />
            )}
          </fieldset>
        </UitkSpacing>
      )}
    </>
  );
};

export const Radios = observer(({ handleChange, model, modelKey, sortAndFilterOptions, rfrrSuffix }) => {
  const singleColumnNames = ['guestRating'];
  const radioGroup: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const radioGroupOptions: FilterOptionModel[] = radioGroup && radioGroup.options;
  const radioGroupName = radioGroup?.title;
  const radioGroupAltName = radioGroup?.name;
  const value = model?.[radioGroupName] || model?.[radioGroupAltName] || '';
  const group = radioGroup.label || radioGroupName;

  let hiddenField;
  if (value) {
    if (!radioGroupOptions.some(option => option.value === value)) {
      hiddenField = <input type="radio" hidden={true} name={radioGroupName} checked={false} value={value} />;
    }
  }

  const getRadioButtons = (columns: UitkLayoutGridColumnRow) => (
    <UitkRadioGroup group={group} onChange={handleChange} selected={value}>
      <UitkSpacing padding={{ inline: 'one' }}>
        <UitkLayoutGrid columns={columns} data-stid={`radio-group-columns-${columns}`}>
          {radioGroupOptions.map((option, idx) => (
            <UitkLayoutGridItem key={`${radioGroupName}-item-${idx}`}>
              <div>
                <RadioButtonTracked
                  description={option.description}
                  events="click"
                  id={`radio-${radioGroupName}-${option.value}`}
                  key={`${radioGroupName}-${idx}-${option.value}`}
                  linkName={`${radioGroupName}.${option.value}`}
                  name={radioGroupName}
                  rfrr={`${radioGroupName}.${option.value}${rfrrSuffix(option.value)}`}
                  value={option.value}
                  data-stid={`radio-${radioGroupName}-${option.value}`}
                  group={radioGroup.label}
                  onChange={handleChange}
                  checked={value === option.value}
                >
                  {option.label}
                </RadioButtonTracked>
              </div>
            </UitkLayoutGridItem>
          ))}
        </UitkLayoutGrid>
      </UitkSpacing>
    </UitkRadioGroup>
  );

  const getRadioButtonLayout = () => (
    <Viewport>
      <ViewSmall>{getRadioButtons(singleColumnNames.includes(radioGroupName) ? 1 : 2)}</ViewSmall>
      <ViewLarge>{getRadioButtons(1)}</ViewLarge>
    </Viewport>
  );

  return (
    <React.Fragment>
      <Experiment name="PWA_JPSmallCrafts_HoldOut">
        <ExperimentControl>{getRadioButtons(1)}</ExperimentControl>
        <ExperimentVariant bucket={1}>{getRadioButtonLayout()}</ExperimentVariant>
        <ExperimentVariant bucket={2}>{getRadioButtonLayout()}</ExperimentVariant>
      </Experiment>
      {hiddenField}
    </React.Fragment>
  );
});

export const RadioGroup = FormFieldBinding(RadioGroupComponent);
